import type {CSSProperties, MutableRefObject, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {toggleGridItemSelection, toggleGridScrollState, updateMaxSelectionNumberForGrid} from '@Components/base-grid/base-grid-reducer';
import {getGridItemForStoredData} from '@Components/base-grid/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {Dimensions} from '@floating-ui/react';
import {noop} from '@Utils/general.util';
import type {ElementTestId} from '@Tests/tests.types';
import styles from './base-masonry-grid.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

const BOTTOM_SCROLL_OFFSET = 100;
const ITEM_DEFAULT_HEIGHT = 110;
const ITEM_MAX_WIDTH = 250;
const ITEM_MIN_WIDTH = 64;
const ITEM_MAX_ASPECT_RATIO = 4;
const ITEM_MIN_ASPECT_RATIO = 0.5;
const FLEX_GROW_FACTOR = 30;
const FLEX_BASIS_FACTOR = 60;
const ITEMS_GAP = 12;
const GRID_WIDTH_THRESHOLD = 50;

export interface BaseMasonryGridProps {
  gridId: string;
  gridSelectionGroupId?: string;
  scrollingContainer?: HTMLDivElement | null;
  className?: string;
  gridContainerClassName?: string;
  rowClassName?: string;
  maxSelectionNumber?: number;
  excludeItemWithExtension?: string;
  makeContentScrollable?: boolean;
  customDimensions?: Dimensions;
  showHoverOverlay?: boolean;
  defaultHeight?: number;
  'data-testid'?: ElementTestId;
  'data-test-load-more'?: boolean;

  attachScrollListener?(cb: () => void): void;

  customOnGridItemClicked?(item: GridItemStorage, isDoubleClick: boolean): void;

  onScrolledToBottom?(): void;

  gridRef?(ref: HTMLDivElement | null): void;
}

export function BaseMasonryGrid({
  className = '',
  gridContainerClassName = '',
  rowClassName = '',
  onScrolledToBottom = (): void => {},
  excludeItemWithExtension = '',
  scrollingContainer = null,
  makeContentScrollable = true,
  gridRef = noop,
  customDimensions = undefined,
  showHoverOverlay = true,
  defaultHeight = ITEM_DEFAULT_HEIGHT,
  ...props
}: BaseMasonryGridProps): ReactElement {
  const [containerWidth, setContainerWidth] = useState(0);
  const selectionGroupId = props.gridSelectionGroupId ?? props.gridId;
  const gridContainer: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
  const gridEl = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const gridData = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId] ?? {};
  });
  const scrollEndTimer = useRef<number | undefined>();

  useEffect(() => {
    attachScrollToBottom();
    if (gridContainer.current) {
      new ResizeObserver(() => {
        setContainerWidth(gridContainer.current?.offsetWidth ?? 0);
      }).observe(gridContainer.current);
    }

    return () => {
      const gridScrollingContainer = getScrollingContainer();
      if (gridScrollingContainer) {
        const gridContainerJQuery = $(gridScrollingContainer);
        gridContainerJQuery.off('scroll');
      }
    };
  }, [gridData]);

  useEffect(() => {
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: selectionGroupId,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
  }, [props.maxSelectionNumber]);

  const toggleScrollState = (isScrolling: boolean): void => {
    dispatch(
      toggleGridScrollState({
        gridId: props.gridId,
        isScrolling: isScrolling,
      })
    );
  };

  const onScroll = (): void => {
    if (scrollEndTimer.current) {
      clearTimeout(scrollEndTimer.current);
    }
    toggleScrollState(true);

    scrollEndTimer.current = window.setTimeout(() => {
      toggleScrollState(false);
    }, 500);
  };

  const attachScrollToBottom = (): void | null => {
    if (props.attachScrollListener) {
      return props.attachScrollListener(onScrolledToBottom);
    }

    const gridScrollingContainer = getScrollingContainer();
    if (gridScrollingContainer) {
      const gridScrollingContainerJQuery = $(gridScrollingContainer);

      gridScrollingContainerJQuery.on(
        'scroll',
        window.PMW.debounce(() => {
          onScroll();
          if (hasGridScrolledToBottom()) {
            void onScrolledToBottom();
          }
        }, 30)
      );
    }
    return null;
  };

  const getScrollingContainer = (): HTMLDivElement | null => {
    if (scrollingContainer) {
      return scrollingContainer;
    }
    return gridContainer.current;
  };

  const hasGridScrolledToBottom = (): boolean => {
    const gridScrollingContainer = getScrollingContainer();
    if (gridScrollingContainer && gridEl.current) {
      const gridElJQuery = $(gridEl.current);
      const gridScrollingContainerJQuery = $(gridScrollingContainer);

      const gridScrollingContainerHeight = gridScrollingContainerJQuery.height();
      const gridContainerScrollPosition = gridScrollingContainerJQuery.scrollTop();
      const gridHeight = gridElJQuery.height();

      if (gridHeight === undefined || gridScrollingContainerHeight === undefined || gridContainerScrollPosition === undefined) {
        return false;
      }

      if (gridHeight - gridScrollingContainerHeight - gridContainerScrollPosition <= BOTTOM_SCROLL_OFFSET) {
        return true;
      }
    }
    return false;
  };

  const onGridItemClicked = (item: GridItemStorage, e: React.MouseEvent, isDoubleClick: boolean): void => {
    if (props.customOnGridItemClicked !== undefined) {
      props.customOnGridItemClicked(item, isDoubleClick);
      return;
    }
    dispatch(
      toggleGridItemSelection({
        gridItemId: item.id,
        gridSelectionGroupId: selectionGroupId,
      })
    );
  };

  const isExtensionAllowed = (ext: string): boolean => {
    if (!excludeItemWithExtension) {
      return true;
    }
    return ext !== excludeItemWithExtension;
  };

  const getGridItemStyles = (aspectRatio: number, maxWidth: number, isFixedWithItem: boolean): CSSProperties => {
    if (customDimensions || isFixedWithItem) {
      return {
        width: maxWidth ? `${maxWidth}px` : '100%',
        height: customDimensions?.height ? `${customDimensions?.height}px` : '100%',
      };
    }
    if (aspectRatio > ITEM_MAX_ASPECT_RATIO || aspectRatio < ITEM_MIN_ASPECT_RATIO) {
      return {};
    }
    return {
      flexGrow: aspectRatio * FLEX_GROW_FACTOR,
      flexBasis: aspectRatio * FLEX_BASIS_FACTOR,
    };
  };

  const getInvalidatedItemWidth = (itemWidth?: number, itemHeight?: number): number => {
    let width = itemWidth ?? defaultHeight;
    const height = itemHeight ?? defaultHeight;
    const aspectRatio = width / height;
    if (aspectRatio > ITEM_MAX_ASPECT_RATIO) {
      width = ITEM_MAX_WIDTH;
    } else if (aspectRatio < ITEM_MIN_ASPECT_RATIO) {
      width = ITEM_MIN_WIDTH;
    } else {
      width = (customDimensions ? customDimensions.height : defaultHeight) * aspectRatio;
    }
    return width;
  };

  const getAllowedWidthForFixedHeightItem = (width?: number): number => {
    const itemWidth = width ?? ITEM_MAX_WIDTH;
    return (containerWidth - (Math.floor(containerWidth / itemWidth) - 1) * ITEMS_GAP) / Math.floor(containerWidth / itemWidth);
  };

  const getGridItemElement = (item: GridItemStorage, itemsMaxWidth: number): ReactElement | null => {
    if (item.extension && !isExtensionAllowed(item.extension as string)) {
      return null;
    }
    const isFixedWithItem = item.type === GRID_ITEM_TYPE.STOCK_AUDIO;
    const width = isFixedWithItem ? (item.width as number) : getInvalidatedItemWidth(item.width as number, item.height as number) / defaultHeight;
    return getGridItemForStoredData({
      item,
      paddedThumb: false,
      gridId: props.gridId,
      gridSelectionGroupId: selectionGroupId,
      style: getGridItemStyles(width, itemsMaxWidth, isFixedWithItem),
      showSelectionNumber: props?.maxSelectionNumber !== 1,
      showHoverOverlay,
      onClicked: onGridItemClicked.bind(null, item),
      className: `flex-center`,
      isSmallItem: true,
      hasTransparency: !!item.hasTransparency,
      containerClassName: styles.gridItemContainer,
    });
  };

  const getRowElementStyles = (noOfItems: number, itemsWidths?: number): CSSProperties => {
    if (customDimensions) {
      return {};
    }
    if (!itemsWidths) {
      return {
        height: defaultHeight,
      };
    }
    return {
      height: (containerWidth - noOfItems * ITEMS_GAP) / (itemsWidths / defaultHeight),
    };
  };

  const getRowElement = (rowIndex: number, rowItems: Array<ReactElement>, itemsWidths?: number): ReactElement => {
    return (
      <ul
        key={`gridRow${rowIndex}`}
        style={getRowElementStyles(rowItems.length, itemsWidths)}
        className={`spacing-m-0 flexbox ${itemsWidths && !customDimensions ? '_full-width' : ''} ${styles.rowContainer} ${rowClassName}`}
      >
        {rowItems}
      </ul>
    );
  };

  const areItemsExceedingAvailableWidth = (itemsWidth: number, noOfItems: number): boolean => {
    return itemsWidth + ITEMS_GAP * noOfItems + GRID_WIDTH_THRESHOLD > containerWidth;
  };

  const getItems = (): Array<ReactElement> | undefined => {
    if (!(gridContainer.current && gridData.items && gridData.items.length)) {
      return undefined;
    }
    let tempRowItems: Array<ReactElement> = [];
    const updatedRowData: Array<ReactElement> = [];
    let itemsWidths = 0;
    let rowIndex = 1;
    const maxPossibleWidth = getAllowedWidthForFixedHeightItem(customDimensions?.width);

    for (const item of gridData.items) {
      const gridItem = getGridItemElement(item, maxPossibleWidth);
      if (gridItem) {
        itemsWidths += customDimensions || item.type === GRID_ITEM_TYPE.STOCK_AUDIO ? maxPossibleWidth : getInvalidatedItemWidth(item.width as number, item.height as number);
        tempRowItems.push(gridItem);
        if (areItemsExceedingAvailableWidth(itemsWidths, tempRowItems.length - 1)) {
          updatedRowData.push(getRowElement(rowIndex, tempRowItems, itemsWidths));
          rowIndex += 1;
          tempRowItems = [];
          itemsWidths = 0;
        }
      }
    }

    if (tempRowItems.length) {
      updatedRowData.push(getRowElement(rowIndex, tempRowItems));
    }
    return updatedRowData;
  };

  return (
    <div
      className={`flex-row-justify-center _full-width ${className} ${makeContentScrollable ? styles.multiMediaScrollContainer : ''}`}
      key={props.gridId}
      ref={(ref): void => {
        gridContainer.current = ref;
        gridRef(ref);
      }}
      data-testid={props['data-testid']}
      data-test-load-more={props['data-test-load-more']}
    >
      <div className={`flexbox _full-width ${styles.gridContainer} ${gridContainerClassName}`} ref={gridEl}>
        {getItems()}
      </div>
    </div>
  );
}
